<i18n>
{
  "en": {
    "name": "Event name",
    "description": "Description",
    "createEventTitle": "Creating event",
    "editEventTitle": "Editing event",
    "emptyFieldError": "Required field",
    "assignPos": "Assign restaurant",
    "allowedPointsOfSales":  "Select restaurants",
    "important": "Important",
    "errorAllowedPosNotIncluded": "The event has a restaurant assigned which you are not allowed to see, therefore if you do not assign any restaurant the event will disappear.",
    "startDate": "Start",
    "endDate": "End",
    "dates": "Dates",
    "eventType": "Choose event type",
    "recurrent": "Recurrent event",
    "typeOfRepetition": "Repetition frequency",
    "endCondition": "End condition",
    "recurrenceEndDate": "Recurrence end date",
    "numberOfRepetitions": "Number of repetitions",
    "daily": "Daily",
    "weekly": "Weekly",
    "monthly": "Monthly",
    "yearly": "Annual",
    "never": "Never",
    "after": "After number of repetitions",
    "onDate": "Until certain date",
    "notBeforeTodayError": "The selected date cannot be earlier than the current date",
    "notBeforeStartDateError": "Cannot be prior to the initial date",
    "dailyRepetitionError": "For daily repetitions, the start and end date must be the same",
    "weeklyRepetitionError": "For weekly repeats, the end date cannot be more than one week after the start date",
    "recurrenceEndDateError": "The recurrence end date cannot be less than the event end date"
  },
  "es": {
    "name": "Nombre de evento",
    "description":  "Descripción",
    "createEventTitle": "Creando evento",
    "editEventTitle": "Editando evento",
    "emptyFieldError": "Campo obligatorio",
    "assignPos":  "Asignar restaurante",
    "allowedPointsOfSales":  "Seleccionar restaurantes",
    "important":  "Importante",
    "errorAllowedPosNotIncluded":  "El evento tiene un restaurante asignado el cual usted no tiene permitido ver, por lo tanto si no le asigna ningun restaurante el evento desaparecera.",
    "startDate": "Inicio",
    "endDate": "Fin",
    "dates": "Fechas",
    "eventType": "Escoger tipo de evento",
    "recurrent": "Evento recurrente",
    "typeOfRepetition": "Frecuencia de repetición",
    "endCondition": "Condición de fin",
    "recurrenceEndDate": "Fecha de fin de recurrencia",
    "numberOfRepetitions": "Número de repeticiones",
    "daily": "Diario",
    "weekly": "Semanal",
    "monthly": "Mensual",
    "yearly": "Anual",
    "never": "Nunca",
    "after": "Después de un número de repeticiones",
    "onDate": "Hasta cierta fecha",
    "notBeforeTodayError": "La fecha seleccionada no puede ser anterior a la fecha actual",
    "notBeforeStartDateError": "No puede ser anterior a la fecha inicial",
    "dailyRepetitionError": "Para repeticiones diarias, la fecha de inicio y fin deben ser iguales",
    "weeklyRepetitionError": "Para repeticiones semanales, la fecha de fin no puede ser más de una semana después de la fecha de inicio",
    "recurrenceEndDateError": "La fecha de fin de recurrencia no puede ser menor que la fecha de fin del evento"
  }
}
</i18n>
<template>
  <v-dialog v-model="visible" max-width="600px" scrollable persistent>
    <v-card v-if="visible" scrollable persistent class="px-7 py-4">
      <v-card-title class="pa-0 mb-2">
        {{ modalTitle }}
      </v-card-title>
      <v-sheet style="background-color: #ffffff">
        <v-form v-model="valid" ref="form">
          <!-- Name and description -->
          <v-text-field :disabled="type === 'edit'"
                        v-model="event.name" :rules="nameRules"
                        :label="$t('name')" class="required"/>
          <v-text-field v-model="event.description" :rules="descriptionRules"
                        :label="$t('description')" class="required"/>
          <!-- Dates of start and end -->
          <v-subheader>{{ $t('dates') }}</v-subheader>
          <v-row justify="space-around">
            <v-col cols="12" sm="6">
              <v-menu ref="startMenu" v-model="startMenu" :close-on-content-click="false"
                :return-value.sync="event.startDate" transition="scale-transition"
                offset-y min-width="290px">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="event.startDate" :label="$t('startDate')" prepend-icon="mdi-calendar"
                    readonly v-bind="attrs" v-on="on" :rules="startDateRules" class="required"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="event.startDate" no-title scrollable>
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="startMenu = false">
                    Cancel
                  </v-btn>
                  <v-btn text color="primary" @click="$refs.startMenu.save(event.startDate)">
                    OK
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6">
              <v-menu ref="endMenu" v-model="endMenu" :close-on-content-click="false"
                :return-value.sync="event.endDate" transition="scale-transition"
                offset-y min-width="290px">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="event.endDate" :label="$t('endDate')" prepend-icon="mdi-calendar"
                    readonly v-bind="attrs" v-on="on" :rules="endDateRules" class="required"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="event.endDate" no-title scrollable>
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="endMenu = false">
                    Cancel
                  </v-btn>
                  <v-btn text color="primary" @click="$refs.endMenu.save(event.endDate)">
                    OK
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <v-divider class="mb-3"/>
          <!-- Event type and point of sale -->
          <v-select v-model="event.eventTypeGuid"
                    :items="eventTypeList" item-value="guid"
                    item-text="name" :label="$t('eventType')"
                    class="required"/>
          <v-select v-model="event.posGuidList"
                    :items="pointOfSaleList" item-value="guid"
                    item-text="name"
                    :label="$t('assignPos')"
                    class="required" :rules="listRules"
                    multiple/>
          <!-- Recurrent events -->
          <v-divider class="mb-3"/>
          <v-switch v-model="event.recurrent" :label="$t('recurrent')"/>
          <!-- Recurrence parameters -->
          <v-select
            v-if="event.recurrent"
            v-model="event.typeOfRepetition" :items="repetitionTypes"
            item-value="value" item-text="name"
            :label="$t('typeOfRepetition')" />
          <v-select
            v-if="event.recurrent"
            v-model="event.endCondition" :items="endConditions"
            item-value="value" item-text="name"
            :label="$t('endCondition')" />
          <v-text-field
            v-if="event.recurrent && event.endCondition === 'on_date'"
            v-model="event.recurrenceEndDate"
            :label="$t('recurrenceEndDate')"
            type="date"/>
          <v-text-field
            v-if="event.recurrent && event.endCondition === 'after'"
            v-model="event.numberOfRepetitions"
            :label="$t('numberOfRepetitions')"
            type="number"/>
          <!-- Dynameat confirmation buttons -->
          <dynameat-modal-buttons class="mt-3"
                                  @cancel="onCancel" @confirm="onConfirm"
                                  :confirmDisabled="!valid"/>
        </v-form>
      </v-sheet>
    </v-card>
  </v-dialog>
</template>
<script>
import {
  notEmptyList,
  notEmptyString
} from '@core/helpers/formValidators';
import Component from 'vue-class-component';
import { Components, MapGetters } from '@core/helpers/decorators';
import { Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import { Event } from '@eventManager/models';

@Component
@Components({ DynameatModalButtons })
@MapGetters(['pointOfSaleList', 'eventTypeList', 'userSettings'])
export default class EventCreateEditModal extends Vue {
  @Prop({
    Object,
    validator(event) {
      return event instanceof Event;
    }
  }) event;

  @Prop({
    String,
    default: 'create',
    validator(type) {
      return type === 'create' || type === 'edit';
    }
  }) type;

  @Prop({ Boolean, default: false }) visible;

  valid = false;

  startMenu = false;

  endMenu = false;

  get repetitionTypes() {
    return [
      { value: 'daily', name: this.$t('daily') },
      { value: 'weekly', name: this.$t('weekly') },
      { value: 'monthly', name: this.$t('monthly') },
      { value: 'yearly', name: this.$t('yearly') }
    ];
  }

  get endConditions() {
    return [
      { value: 'never', name: this.$t('never') },
      { value: 'on_date', name: this.$t('onDate') },
      { value: 'after', name: this.$t('after') }
    ];
  }

  get modalTitle() {
    return this.$t(`${this.type}EventTitle`);
  }

  get nameRules() {
    return [notEmptyString(this.$t('emptyFieldError'))];
  }

  get descriptionRules() {
    return [notEmptyString(this.$t('emptyFieldError'))];
  }

  get listRules() {
    return [notEmptyList(this.$t('emptyFieldError'))];
  }

  get startDateRules() {
    return [
      notEmptyString(this.$t('emptyFieldError')),
      this.notBeforeToday
    ];
  }

  notBeforeToday(value) {
    if (new Date(value) < new Date()) {
      return this.$t('notBeforeTodayError');
    }
    return true;
  }

  get endDateRules() {
    return [
      notEmptyString(this.$t('emptyFieldError')),
      this.notBeforeStartDate,
      this.validEndDateBasedOnRepetition,
      this.notBeforeToday,
      this.recurrenceEndDateValidation
    ];
  }

  notBeforeStartDate(value) {
    if (new Date(value) < new Date(this.event.startDate)) {
      return this.$t('notBeforeStartDateError');
    }
    return true;
  }

  validEndDateBasedOnRepetition(value) {
    if (this.event.typeOfRepetition === 'daily' && new Date(value).getTime() !== new Date(this.event.startDate).getTime()) {
      return this.$t('dailyRepetitionError');
    }
    if (this.event.typeOfRepetition === 'weekly') {
      const oneWeekAfterStartDate = new Date(this.event.startDate);
      oneWeekAfterStartDate.setDate(oneWeekAfterStartDate.getDate() + 7);
      if (new Date(value) > oneWeekAfterStartDate) {
        return this.$t('weeklyRepetitionError');
      }
    }
    return true;
  }

  recurrenceEndDateValidation(value) {
    if (this.event.endCondition === 'on_date' && new Date(this.event.recurrenceEndDate) < new Date(value)) {
      return this.$t('recurrenceEndDateError');
    }
    return true;
  }

  onCancel() {
    this.$emit('cancel');
  }

  onConfirm() {
    if (this.$refs.form.validate()) {
      if (this.event.recurrenceEndDate === '') {
        this.event.recurrenceEndDate = null;
      }
      this.$emit('confirm', this.event);
    }
  }

  @Watch('event', { immediate: true })
  onEventChanged(newEvent) {
    if (newEvent) {
      this.event.guid = newEvent.guid;
      this.event.name = newEvent.name;
      this.event.description = newEvent.description;
      this.event.startDate = newEvent.startDate;
      this.event.endDate = newEvent.endDate;
      this.event.recurrent = newEvent.recurrent;
      this.event.typeOfRepetition = newEvent.typeOfRepetition;
      this.event.endCondition = newEvent.endCondition;
      this.event.recurrenceEndDate = newEvent.recurrenceEndDate;
      this.event.numberOfRepetitions = newEvent.numberOfRepetitions;
      this.event.eventTypeGuid = newEvent.eventTypeGuid;
      this.event.posGuidList = newEvent.posGuidList;
    }
  }
}
</script>
<style scoped>
.error-message-allowed-pod {
  color: red;
  font-size: 12px;
  line-height: 20px;
  padding: 5px 10px 10px 10px;
}
</style>
